import { useMemo, useRef, useState } from 'react';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { Button } from '../ui';
import { contentIngestionService, type IngestionLimits, type IngestionRun } from '../../services/contentIngestionService';

interface SelectedUpload {
  key: string;
  file: File;
  error: string | null;
  uploaded: boolean;
}

const DEFAULT_LIMITS: IngestionLimits = {
  accepted_extensions: ['.csv', '.xlsx'],
  max_files: 50,
  max_file_bytes: 10 * 1024 * 1024,
};

function localError(file: File, limits: IngestionLimits): string | null {
  const suffix = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  if (!limits.accepted_extensions.includes(suffix)) return 'Unsupported file type';
  if (file.size > limits.max_file_bytes) return `File exceeds ${formatBytes(limits.max_file_bytes)}`;
  return null;
}

function formatBytes(bytes: number) {
  if (bytes >= 1024 * 1024 && bytes % (1024 * 1024) === 0) return `${bytes / (1024 * 1024)} MiB`;
  if (bytes >= 1024 && bytes % 1024 === 0) return `${bytes / 1024} KiB`;
  return `${bytes} bytes`;
}

export default function ContentUploadDialog({ eventId, limits = DEFAULT_LIMITS, onRunCreated }: {
  eventId: string;
  limits?: IngestionLimits;
  onRunCreated: (run: IngestionRun) => void;
}) {
  const [files, setFiles] = useState<SelectedUpload[]>([]);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const validCount = useMemo(() => files.filter((item) => !item.error && !item.uploaded).length, [files]);

  function choose(selected: FileList | null) {
    if (!selected) return;
    setSelectionError(selected.length > limits.max_files ? `Choose no more than ${limits.max_files} files at once.` : null);
    const additions = Array.from(selected).slice(0, limits.max_files).map((file, index) => ({
      key: `${file.name}:${file.size}:${file.lastModified}:${index}`,
      file,
      error: localError(file, limits),
      uploaded: false,
    }));
    setFiles(additions);
  }

  async function uploadFiles() {
    const pending = files.filter((item) => !item.error && !item.uploaded);
    if (!pending.length) return;
    setUploading(true);
    try {
      const run = await contentIngestionService.createRun(eventId);
      let uploadedAny = false;
      for (const item of pending) {
        try {
          await contentIngestionService.uploadFiles(eventId, run.id, [item.file]);
          uploadedAny = true;
          setFiles((current) => current.map((entry) => entry.key === item.key ? { ...entry, uploaded: true, error: null } : entry));
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Upload failed';
          setFiles((current) => current.map((entry) => entry.key === item.key ? { ...entry, error: message } : entry));
        }
      }
      if (uploadedAny) onRunCreated(await contentIngestionService.run(eventId, run.id));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <section aria-label="Upload event content" className="rounded-2xl border border-gray-800 bg-gray-900 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-white">Add event content</h2>
          <p className="mt-1 text-sm text-gray-400">Upload schedules or speaker lists. You will review mappings before anything becomes live.</p>
        </div>
        <FileSpreadsheet className="text-sky-400" size={22} />
      </div>
      <div className="mt-4 rounded-xl border border-dashed border-gray-700 bg-gray-950/50 p-4">
        <input
          ref={inputRef}
          aria-label="Choose content files"
          type="file"
          multiple
          accept={limits.accepted_extensions.join(',')}
          onChange={(event) => choose(event.target.files)}
          className="block w-full text-sm text-gray-300 file:mr-3 file:rounded-lg file:border-0 file:bg-sky-500/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-sky-300"
        />
        <div className="mt-2 flex gap-4 text-xs text-gray-500"><span>{limits.accepted_extensions.join(', ')}</span><span>{limits.max_files} files maximum</span><span>{formatBytes(limits.max_file_bytes)} per file</span></div>
      </div>
      {selectionError && <p role="alert" className="mt-3 text-sm text-red-300">{selectionError}</p>}
      {files.length > 0 && <ul className="mt-4 space-y-2" aria-label="Selected files">
        {files.map((item) => <li key={item.key} className="flex items-center justify-between gap-3 rounded-lg border border-gray-800 px-3 py-2 text-sm">
          <span className="truncate text-gray-200">{item.file.name}</span>
          {item.uploaded ? <span className="text-emerald-400">Uploaded</span> : item.error ? <span role="alert" className="text-red-300">{item.error}</span> : <span className="text-gray-500">Ready</span>}
        </li>)}
      </ul>}
      <div className="mt-4 flex justify-end">
        <Button icon={<Upload size={14} />} loading={uploading} disabled={!validCount || uploading} onClick={() => void uploadFiles()}>Upload files</Button>
      </div>
    </section>
  );
}
